import { observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { TravelerInformationResponseArgs } from './traveler_information';
import TravelerInformation, { convertTravelerInformationResponseToArgs } from './traveler_information';

export type TransportElementReservationResponseArgs = {
  id: number;
  transport_element_id: number;
  status: string | number; // 'waiting' | 'reserved' | 'reservation_info_gotten' | 'ticket_issued' | 'occupied' | 'failure' | 'unsupport'
  traveler_information_id?: number;
  failure_type?: string | number; // 'flight_not_found' | 'insert_error' | 'others'
  retry_count?: number;
  retry_message?: string;
  smart_ticket_rsv_id?: string;
  smart_ticket_air_company_code?: string;
  smart_ticket_ticket_name?: string;
  smart_ticket_ticket_code?: string;
  smart_ticket_adult_price?: number;
  reserved_at?: string;
  smart_ticket_reservation_number?: string;
  smart_ticket_expired_date?: string;
  smart_ticket_amount?: number;
  reservation_info_gotten_at?: string;
  smart_ticket_confirmation_number?: string;
  smart_ticket_pdf_url?: string;
  ticket_issued_at?: string;
  created_at?: string;
  updated_at?: string;
  error_message?: string;
  traveler_information?: TravelerInformationResponseArgs;
};

export type TransportElementReservationArgs = {
  id: number;
  transportElementId: number;
  status: string | number;
  travelerInformationId?: number;
  failureType?: string | number;
  retryCount?: number;
  retryMessage?: string;
  smartTicketRsvId?: string;
  smartTicketAirCompanyCode?: string;
  smartTicketTicketName?: string;
  smartTicketTicketCode?: string;
  smartTicketAdultPrice?: number;
  reservedAt?: Moment;
  smartTicketReservationNumber?: string;
  smartTicketExpiredDate?: string;
  smartTicketAmount?: number;
  reservationInfoGottenAt?: Moment;
  smartTicketConfirmationNumber?: string;
  smartTicketPdfUrl?: string;
  ticketIssuedAt?: Moment;
  createdAt?: Moment;
  updatedAt?: Moment;
  errorMessage?: string;
  travelerInformation?: TravelerInformation;
};

export const convertTransportElementReservationResponseToArgs = (
  response: TransportElementReservationResponseArgs
): TransportElementReservationArgs => {
  return {
    id: response.id,
    transportElementId: response.transport_element_id,
    status: response.status,
    travelerInformationId: response.traveler_information_id,
    failureType: response.failure_type,
    retryCount: response.retry_count,
    retryMessage: response.retry_message,
    smartTicketRsvId: response.smart_ticket_rsv_id,
    smartTicketAirCompanyCode: response.smart_ticket_air_company_code,
    smartTicketTicketName: response.smart_ticket_ticket_name,
    smartTicketTicketCode: response.smart_ticket_ticket_code,
    smartTicketAdultPrice: response.smart_ticket_adult_price,
    reservedAt: response.reserved_at ? moment(response.reserved_at) : undefined,
    smartTicketReservationNumber: response.smart_ticket_reservation_number,
    smartTicketExpiredDate: response.smart_ticket_expired_date,
    smartTicketAmount: response.smart_ticket_amount,
    reservationInfoGottenAt: response.reservation_info_gotten_at
      ? moment(response.reservation_info_gotten_at)
      : undefined,
    smartTicketConfirmationNumber: response.smart_ticket_confirmation_number,
    smartTicketPdfUrl: response.smart_ticket_pdf_url,
    ticketIssuedAt: response.ticket_issued_at ? moment(response.ticket_issued_at) : undefined,
    createdAt: response.created_at ? moment(response.created_at) : undefined,
    updatedAt: response.updated_at ? moment(response.updated_at) : undefined,
    errorMessage: response.error_message,
    travelerInformation: response.traveler_information
      ? new TravelerInformation(convertTravelerInformationResponseToArgs(response.traveler_information))
      : undefined
  };
};

export default class TransportElementReservation {
  @observable id: number;
  @observable transportElementId: number;
  @observable status: string | number;
  @observable travelerInformationId?: number;
  @observable failureType?: string | number;
  @observable retryCount?: number;
  @observable retryMessage?: string;
  @observable smartTicketRsvId?: string;
  @observable smartTicketAirCompanyCode?: string;
  @observable smartTicketTicketName?: string;
  @observable smartTicketTicketCode?: string;
  @observable smartTicketAdultPrice?: number;
  @observable reservedAt?: Moment;
  @observable smartTicketReservationNumber?: string;
  @observable smartTicketExpiredDate?: string;
  @observable smartTicketAmount?: number;
  @observable reservationInfoGottenAt?: Moment;
  @observable smartTicketConfirmationNumber?: string;
  @observable smartTicketPdfUrl?: string;
  @observable ticketIssuedAt?: Moment;
  @observable createdAt?: Moment;
  @observable updatedAt?: Moment;
  @observable errorMessage?: string;
  @observable travelerInformation?: TravelerInformation;

  constructor(args: TransportElementReservationArgs) {
    this.id = args.id;
    this.transportElementId = args.transportElementId;
    this.status = args.status;
    this.travelerInformationId = args.travelerInformationId;
    this.failureType = args.failureType;
    this.retryCount = args.retryCount;
    this.retryMessage = args.retryMessage;
    this.smartTicketRsvId = args.smartTicketRsvId;
    this.smartTicketAirCompanyCode = args.smartTicketAirCompanyCode;
    this.smartTicketTicketName = args.smartTicketTicketName;
    this.smartTicketTicketCode = args.smartTicketTicketCode;
    this.smartTicketAdultPrice = args.smartTicketAdultPrice;
    this.reservedAt = args.reservedAt;
    this.smartTicketReservationNumber = args.smartTicketReservationNumber;
    this.smartTicketExpiredDate = args.smartTicketExpiredDate;
    this.smartTicketAmount = args.smartTicketAmount;
    this.reservationInfoGottenAt = args.reservationInfoGottenAt;
    this.smartTicketConfirmationNumber = args.smartTicketConfirmationNumber;
    this.smartTicketPdfUrl = args.smartTicketPdfUrl;
    this.ticketIssuedAt = args.ticketIssuedAt;
    this.createdAt = args.createdAt;
    this.updatedAt = args.updatedAt;
    this.errorMessage = args.errorMessage;
    this.travelerInformation = args.travelerInformation;
  }
}
